<template>
  <header class="header">
    <div class="header__content">
      <router-link :to="{ name: 'NomenclaturePage' }">
        <img
          class="header__logo-desktop"
          alt="logo-decktop"
          src="../../assets/logo.svg"
        />
      </router-link>
      <div
        class="header__content--menu"
        v-if="user?.newbie && isAuth?.is_active"
      >
        <router-link
          :to="{ name: 'NomenclaturePage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'NomenclaturePage' }"
        >
          <div class="header__menu--item">
            <div class="header__menu--circle">1</div>
            <p class="header__menu--title">Номенклатура</p>
          </div>
        </router-link>

        <router-link
          v-if="isRulesEnabled"
          :to="{ name: 'RulesPage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'RulesPage' }"
        >
          <div class="header__menu--item">
            <div class="header__menu--circle">2</div>
            <p class="header__menu--title">Правила упаковки</p>
          </div>
        </router-link>
        <div v-else class="header__menu--link no-select">
          <div class="header__menu--item">
            <div class="header__menu--circle">2</div>
            <p class="header__menu--title">Правила упаковки</p>
          </div>
        </div>

        <router-link
          v-if="isCalculationsEnabled"
          :to="{ name: 'CalculationsPage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'CalculationsPage' }"
        >
          <div class="header__menu--item">
            <div class="header__menu--circle">3</div>
            <p class="header__menu--title">Расчёты</p>
          </div>
        </router-link>
        <div v-else class="header__menu--link no-select">
          <div class="header__menu--item">
            <div class="header__menu--circle">3</div>
            <p class="header__menu--title">Расчёты</p>
          </div>
        </div>
      </div>
      <div class="header__content--menu" v-else-if="isAuth?.is_active">
        <router-link
          :to="{ name: 'NomenclaturePage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'NomenclaturePage' }"
        >
          <div class="header__menu--item">
            <p class="header__menu--title">Номенклатура</p>
          </div>
        </router-link>

        <router-link
          :to="{ name: 'RulesPage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'RulesPage' }"
        >
          <div class="header__menu--item">
            <p class="header__menu--title">Правила упаковки</p>
          </div>
        </router-link>

        <router-link
          :to="{ name: 'CalculationsPage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'CalculationsPage' }"
        >
          <div class="header__menu--item">
            <p class="header__menu--title">Расчёты</p>
          </div>
        </router-link>

        <router-link
          :to="{ name: 'LogsPage' }"
          class="header__menu--link"
          :class="{ active: route.name === 'LogsPage' }"
        >
          <div class="header__menu--item">
            <p class="header__menu--title">История</p>
          </div>
        </router-link>
      </div>
      <div class="header__btn-group">
        <div class="header__login--name">{{ user?.email }}</div>
        <div
          v-if="isAuth"
          class="header__popover-wrapper"
          @mouseenter="showPopover = true"
          @mouseleave="showPopover = false"
        >
          <div class="header__popover-container">
            <div class="link">
              <iconProfile />
            </div>

            <div class="header__popover" :class="{ visible: showPopover }">
              <button
                @click="showСhangePasswordModal()"
                class="header__popover-button link"
              >
                Изменить пароль
              </button>

              <button
                v-if="!user?.is_active"
                @click="showSubmitAccount()"
                class="header__popover-button link"
              >
                Подтвердить аккаунт
              </button>

              <button
                @click="showDeleteModal()"
                class="header__popover-button link"
              >
                Удалить аккаунт
              </button>

              <div class="header__divider"></div>
              <button @click="logout" class="header__popover-button link">
                Выйти
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>

  <ChangeEmailModal ref="changePasswordRef" />

  <DeleteModal :dataProps="user" ref="deleteModalRef" @submit="remove">
    Удалить аккаунт пользователя {{ user?.email }}?
  </DeleteModal>
</template>

<script lang="ts">
import { defineComponent, ref, computed, reactive, onMounted } from 'vue';
import ChangeEmailModal from '@/components/header/ChangeEmailModal.vue';
import { useAuthStore } from '@/store/authStore';
import { useDataStore } from '@/store/dataStore';
import { useRoute, useRouter } from 'vue-router';
import type { userType, modalType, INomenclature, IRule } from '@/types/common';
import iconProfile from '@/components/icons/iconProfile.vue';
import DeleteModal from '@/pages/modalsProfile/DeleteModal.vue';
import * as authService from '@/api/authService';
import { showError, showMessage } from '@/utils/errorsInterceptor';

interface IState {
  preloader: boolean;
}

export default defineComponent({
  name: 'AuthHeaderComponent',
  components: {
    ChangeEmailModal,
    iconProfile,
    DeleteModal,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const dataStore = useDataStore();
    const isAuth = computed((): userType | null => authStore.userStore);
    const user = computed((): userType | null => authStore.getUser);
    const showPopover = ref<boolean>(false);
    const nomenclatures = computed(
      (): INomenclature[] => dataStore.nomenclatures
    );
    const rules = computed((): IRule[] => dataStore.rules);

    const state: IState = reactive({
      preloader: false,
    });
    const showСhangePasswordModal = () => {
      if (changePasswordRef.value) {
        changePasswordRef.value.isOpen = true;
      }
    };

    const showDeleteModal = () => {
      if (deleteModalRef.value) {
        deleteModalRef.value.isOpen = true;
      }
    };

    const showSubmitAccount = () => {
      if (submitAccountRef.value) {
        submitAccountRef.value.isOpen = true;
      }
    };

    const remove = async () => {
      state.preloader = true;
      try {
        const response = await authService.removeUser();
        state.preloader = false;
        showMessage('Аккаунт успешно удален');
        router.push({ name: 'HomePage' });
        authStore.logout();
      } catch (error: any) {
        state.preloader = false;
        showError(error);
      }
    };

    const logout = () => {
      router.push({ name: 'HomePage' });
      authStore.logout();
    };

    const changeEmailRef = ref<modalType | null>(null);
    const changePasswordRef = ref<modalType | null>(null);
    const deleteModalRef = ref<modalType | null>(null);
    const submitAccountRef = ref<modalType | null>(null);

    const isRulesEnabled = computed(
      (): boolean => nomenclatures.value?.length > 0
    );

    const isCalculationsEnabled = computed(
      (): boolean => nomenclatures.value?.length > 0 && rules.value?.length > 0
    );

    const init = () => {
      if (isAuth.value?.is_active === true) {
        dataStore.loadMainData();
      } else {
        showSubmitAccount();
      }
    };
    init();

    return {
      showСhangePasswordModal,
      showDeleteModal,
      logout,
      remove,
      route,
      changePasswordRef,
      changeEmailRef,
      isAuth,
      user,
      showPopover,
      nomenclatures,
      rules,
      isRulesEnabled,
      isCalculationsEnabled,
      deleteModalRef,
      submitAccountRef,
      showSubmitAccount,
    };
  },
});
</script>

<style lang="scss" scoped>
.header {
  background-color: var(--background-2);
  width: 100%;
  border-bottom: 1px solid var(--primary);
  &__content {
    max-width: 1920px;
    padding-left: 40px;
    padding-right: 40px;
    height: 70px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 auto;
    column-gap: 16px;
    &--menu {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      max-width: 588px;
      width: 100%;
    }
  }
  &__menu {
    &--title {
      color: var(--primary);
      font-family: 'Nunito';
      font-size: 18px;
      font-weight: 700;
      line-height: 22px;
    }

    &--circle {
      width: 23px;
      height: 23px;
      flex-shrink: 0;
      border-radius: 50%;
      background-color: var(--primary);
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      color: #ffffff;
    }
    &--item {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    &--link {
      text-decoration: none;

      &.active .header__menu--title {
        color: var(--primary);
      }

      &.active .header__menu--circle {
        background-color: var(--primary);
      }

      &:not(.active) .header__menu--title {
        color: var(--disabled);
      }

      &:not(.active) .header__menu--circle {
        background-color: var(--disabled);
      }
    }
  }
  &__logo {
    display: flex;
    align-items: center;
    height: 100%;
    column-gap: 8px;
    user-select: none;
    margin: 0;
    &-desktop {
      display: inline-block;
      @media (max-width: 768px) {
        display: none;
      }
    }
  }
  &__btn-group {
    display: flex;
    height: 100%;
    align-items: center;
    column-gap: 16px;
    margin-left: 16px;
  }
  &__aut-btn {
    font-size: 18px;
    font-weight: 400;
    line-height: 22px;
    color: var(--typography-white);
    background-color: var(--primary);
    border: 1px solid var(--primary);
    padding: 8px 9px 8px 9px;
  }
  &__button {
    font-size: 18px;
  }
  &__popover-wrapper {
    position: relative;
    display: inline-block;
    &:hover .header__popover,
    .header__popover:hover {
      display: flex;
    }
  }
  &__popover {
    position: absolute;
    top: calc(100% + 30px);
    left: -112px;
    transform: translateX(-50%);
    width: 290px;
    background-color: white;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    flex-direction: column;
    justify-content: space-around;
    padding: 16px;
    padding-bottom: 8px;
    z-index: 1000;
    &.visible {
      visibility: visible;
      opacity: 1;
    }
  }
  &__popover-button {
    background: none;
    border: none;
    text-align: left;
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    color: var(--primary);
    cursor: pointer;
    transition: background-color 0.2s;
  }
  &__divider {
    width: calc(100% + 32px);
    height: 1px;
    background-color: var(--primary);
    margin: 12px -16px;
  }
  &__login--name {
    color: var(--primary);
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
  }
}
</style>
